import React, { useState } from 'react';
import { useLMS } from '../../context/LMSContext';
import { authHelpers } from '../../lib/supabase';

const UserManagement = () => {
  // FIXED: Changed 'users' to 'students' and updated function names to match LMSContext
  const {
    currentUser,
    students,  // Changed from 'users'
    cohorts,
    addStudent,     // Changed from 'addUser'
    updateStudent,  // Changed from 'updateUser'
    deleteStudent,  // Changed from 'deleteUser'
  } = useLMS();

  // FIXED: Add safety fallback for students array
  const users = students || [];

  // Role-based access: admins get full control, clerical staff can only
  // add/edit students (no role changes, no deletion, no editing of
  // admins/professors/other clericals).
  const isAdminUser = currentUser?.role === 'admin';
  const isClericalUser = currentUser?.role === 'clerical';

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCohort, setFilterCohort] = useState('all');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetEmailError, setResetEmailError] = useState('');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'student',
    cohortId: '',
    method: 'invite', // 'invite' sends email, 'create' makes temp password
  });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserSuccess, setAddUserSuccess] = useState(null);

  // Bulk import state
  const [bulkText, setBulkText] = useState('');
  const [bulkCohortId, setBulkCohortId] = useState('');
  const [bulkParsed, setBulkParsed] = useState([]);
  const [bulkStep, setBulkStep] = useState('input'); // 'input' | 'preview' | 'importing' | 'done'
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkProgress, setBulkProgress] = useState(0);

  // FIXED: Add safety checks for filter operations
  const filteredUsers = (users || []).filter(user => {
    const matchesSearch = (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesCohort = filterCohort === 'all' || 
                          (filterCohort === 'unassigned' ? !user.cohortId : user.cohortId === filterCohort);
    return matchesSearch && matchesRole && matchesStatus && matchesCohort;
  });

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserLoading(true);
    setAddUserSuccess(null);

    // Clerical users can only create students — force the role regardless of
    // any form state to guarantee they cannot escalate privileges.
    const payload = isClericalUser ? { ...newUser, role: 'student' } : newUser;

    const result = await addStudent(payload);

    if (result.error) {
      alert(result.error);
      setAddUserLoading(false);
      return;
    }

    // Show success message (especially important for temp password)
    if (result.tempPassword) {
      setAddUserSuccess({
        message: `User created successfully!`,
        tempPassword: result.tempPassword,
        email: newUser.email,
      });
    } else {
      setAddUserSuccess({
        message: result.message || `Invitation sent to ${newUser.email}!`,
      });
    }

    setAddUserLoading(false);
    // Don't close modal yet so admin can see temp password or confirmation
    setNewUser({ name: '', email: '', role: 'student', cohortId: '', method: 'invite' });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();

    // Defense-in-depth: clerical users can only edit students, and cannot
    // change a user's role or status beyond student-level fields.
    let payload = editingUser;
    if (isClericalUser) {
      if (editingUser.role !== 'student') {
        alert('Clerical users can only edit student accounts.');
        return;
      }
      payload = { ...editingUser, role: 'student' };
    }

    const result = await updateStudent(editingUser.id, payload);
    if (result.error) {
      alert(result.error);
      return;
    }
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleDeleteUser = async (userId) => {
    // Clerical users cannot delete users — guard both UI and action.
    if (isClericalUser) {
      alert('Clerical users cannot delete accounts. Please ask an administrator.');
      return;
    }
    if (window.confirm('Are you sure you want to permanently delete this user and all their data? This cannot be undone.')) {
      const result = await deleteStudent(userId);
      if (result.error) {
        alert(result.error);
      }
    }
  };

  // FIXED: toggleUserStatus doesn't exist in context - implement locally using updateStudent
  const toggleUserStatus = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Clerical users cannot change the active/inactive status of non-students.
    if (isClericalUser && user.role !== 'student') {
      alert('Clerical users can only change status for student accounts.');
      return;
    }

    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const result = await updateStudent(userId, { status: newStatus });
    if (result.error) {
      alert(result.error);
    }
  };

  const openEditModal = (user) => {
    // Clerical users cannot view/edit non-student accounts.
    if (isClericalUser && user.role !== 'student') {
      alert('Clerical users can only edit student accounts.');
      return;
    }
    setEditingUser({ ...user });
    setShowEditModal(true);
    setResetEmailSent(false);
    setResetEmailError('');
  };

  const openProfileModal = (user) => {
    setViewingUser(user);
    setShowProfileModal(true);
  };

  const handlePasswordReset = async (email) => {
    setResetEmailSent(false);
    setResetEmailError('');
    
    try {
      const { error } = await authHelpers.resetPassword(email);
      if (error) {
        setResetEmailError(error.message);
      } else {
        setResetEmailSent(true);
      }
    } catch (err) {
      setResetEmailError('Failed to send reset email');
    }
  };

  const getCohortName = (cohortId) => {
    const cohort = (cohorts || []).find(c => c.id === cohortId);
    return cohort ? cohort.name : 'Not assigned';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // =========================================================================
  // BULK IMPORT FUNCTIONS
  // =========================================================================

  const parseBulkText = () => {
    if (!bulkText.trim()) return;

    const lines = bulkText.trim().split('\n').filter(line => line.trim());
    const parsed = [];
    const existingEmails = new Set(users.map(u => (u.email || '').toLowerCase()));

    for (const line of lines) {
      let name = '';
      let email = '';

      if (line.includes('\t')) {
        const parts = line.split('\t').map(p => p.trim());
        name = parts[0] || '';
        email = parts[1] || '';
      } else if (line.includes(',')) {
        const parts = line.split(',').map(p => p.trim());
        if (parts[0].includes('@')) {
          email = parts[0];
          name = email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        } else {
          name = parts[0];
          email = parts[1] || '';
        }
      } else if (line.includes('@')) {
        email = line.trim();
        name = email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      } else {
        parsed.push({ name: line.trim(), email: '', status: 'error', error: 'No email address' });
        continue;
      }

      email = email.toLowerCase().trim();

      if (!email || !email.includes('@')) {
        parsed.push({ name, email, status: 'error', error: 'Invalid email' });
      } else if (existingEmails.has(email)) {
        parsed.push({ name, email, status: 'skip', error: 'Already exists' });
      } else {
        parsed.push({ name, email, status: 'ready', error: null });
        existingEmails.add(email);
      }
    }

    setBulkParsed(parsed);
    setBulkStep('preview');
  };

  const handleBulkImport = async () => {
    const toImport = bulkParsed.filter(p => p.status === 'ready');
    if (toImport.length === 0) return;

    setBulkStep('importing');
    setBulkProgress(0);
    const results = [...bulkParsed];

    for (let i = 0; i < results.length; i++) {
      if (results[i].status !== 'ready') continue;

      try {
        const result = await addStudent({
          name: results[i].name,
          email: results[i].email,
          role: 'student',
          cohortId: bulkCohortId || null,
          method: 'invite',
        });

        if (result.error) {
          results[i] = { ...results[i], status: 'error', error: result.error };
        } else {
          results[i] = { ...results[i], status: 'success', error: null };
        }
      } catch (err) {
        results[i] = { ...results[i], status: 'error', error: err.message || 'Unknown error' };
      }

      setBulkProgress(Math.round(((i + 1) / results.length) * 100));
      setBulkResults([...results]);
    }

    setBulkResults(results);
    setBulkStep('done');
  };

  const resetBulkModal = () => {
    setBulkText('');
    setBulkCohortId('');
    setBulkParsed([]);
    setBulkStep('input');
    setBulkResults([]);
    setBulkProgress(0);
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    resetBulkModal();
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>Manage students and administrators</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowBulkModal(true)}>
            📋 Bulk Import
          </button>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            + Add User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid small">
        <div className="stat-card">
          <div className="stat-icon blue">👥</div>
          <div className="stat-content">
            <div className="stat-value">{users.length}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon green">✅</div>
          <div className="stat-content">
            <div className="stat-value">{users.filter(u => u.status === 'active').length}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon red">🎓</div>
          <div className="stat-content">
            <div className="stat-value">{users.filter(u => u.role === 'student').length}</div>
            <div className="stat-label">Students</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon purple">🔑</div>
          <div className="stat-content">
            <div className="stat-value">{users.filter(u => u.role === 'admin').length}</div>
            <div className="stat-label">Admins</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>🎓</div>
          <div className="stat-content">
            <div className="stat-value">{users.filter(u => u.role === 'professor').length}</div>
            <div className="stat-label">Professors</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fff3e0' }}>📋</div>
          <div className="stat-content">
            <div className="stat-value">{users.filter(u => u.role === 'clerical').length}</div>
            <div className="stat-label">Clerical</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-input">
          <span className="search-icon">🔍</span>
          <input 
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="student">Students</option>
            <option value="professor">Professors</option>
            <option value="clerical">Clerical</option>
            <option value="admin">Admins</option>
          </select>
          
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          <select value={filterCohort} onChange={(e) => setFilterCohort(e.target.value)}>
            <option value="all">All Cohorts</option>
            <option value="unassigned">Unassigned</option>
            {(cohorts || []).map(cohort => (
              <option key={cohort.id} value={cohort.id}>
                {cohort.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Cohort</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-cell">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="user-avatar-img small" />
                    ) : (
                      <div className="user-avatar small">
                        {(user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                    )}
                    <div className="user-details">
                      <div className="user-name">{user.name || 'Unknown'}</div>
                      <div className="user-email">{user.email || ''}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'admin' ? '🔑 Admin'
                      : user.role === 'professor' ? '📖 Professor'
                      : user.role === 'clerical' ? '📋 Clerical'
                      : '🎓 Student'}
                  </span>
                </td>
                <td>
                  {user.role === 'student' || user.role === 'professor' ? (
                    <span className="cohort-badge">{getCohortName(user.cohortId)}</span>
                  ) : (
                    <span className="cohort-badge na">N/A</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${user.status || 'active'}`}>
                    {user.status === 'active' ? '● Active' : '○ Inactive'}
                  </span>
                </td>
                <td className="date-cell">
                  {formatDate(user.createdAt)}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      title="View Profile"
                      onClick={() => openProfileModal(user)}
                    >
                      👤
                    </button>
                    {(!isClericalUser || user.role === 'student') && (
                      <button
                        className="btn-icon"
                        title="Edit User"
                        onClick={() => openEditModal(user)}
                      >
                        ✏️
                      </button>
                    )}
                    {(!isClericalUser || user.role === 'student') && (
                      <button
                        className="btn-icon"
                        title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                        onClick={() => toggleUserStatus(user.id)}
                      >
                        {user.status === 'active' ? '🔒' : '🔓'}
                      </button>
                    )}
                    {!isClericalUser && (
                      <button
                        className="btn-icon danger"
                        title="Delete User"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="empty-table-state">
            <p>No users found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setAddUserSuccess(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="modal-close" onClick={() => { setShowAddModal(false); setAddUserSuccess(null); }}>×</button>
            </div>

            {/* Success feedback */}
            {addUserSuccess && (
              <div style={{ padding: '16px 24px' }}>
                <div className="info-box" style={{ background: '#e8f5e9', borderColor: '#4caf50' }}>
                  <span className="info-icon">✅</span>
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{addUserSuccess.message}</p>
                    {addUserSuccess.tempPassword && (
                      <div style={{ marginTop: 8 }}>
                        <p style={{ fontSize: '0.85em', color: '#555', marginBottom: 4 }}>
                          Temporary password for <strong>{addUserSuccess.email}</strong>:
                        </p>
                        <code style={{ 
                          background: '#f5f5f5', padding: '8px 12px', borderRadius: 4, 
                          display: 'block', fontSize: '1.1em', fontWeight: 600,
                          letterSpacing: '0.5px', userSelect: 'all'
                        }}>
                          {addUserSuccess.tempPassword}
                        </code>
                        <p style={{ fontSize: '0.8em', color: '#888', marginTop: 4 }}>
                          Share this with the student. They should change it after first login.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn-primary" 
                    onClick={() => { setAddUserSuccess(null); }}
                  >
                    Add Another User
                  </button>
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => { setShowAddModal(false); setAddUserSuccess(null); }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}

            {/* Add user form */}
            {!addUserSuccess && (
              <form onSubmit={handleAddUser}>
                <div className="form-group" style={{ padding: '0 24px' }}>
                  <label>Creation Method</label>
                  <select
                    value={newUser.method}
                    onChange={(e) => setNewUser({...newUser, method: e.target.value})}
                    disabled={addUserLoading}
                  >
                    <option value="invite">📧 Send Invitation (user sets their own password)</option>
                    <option value="create">🔑 Manual Create (generates a temp password)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Full Name</label>
                  <input 
                    type="text"
                    placeholder="Jane Smith"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    required
                    disabled={addUserLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email"
                    placeholder="jane@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                    disabled={addUserLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={isClericalUser ? 'student' : newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    disabled={addUserLoading || isClericalUser}
                  >
                    <option value="student">🎓 Student</option>
                    {!isClericalUser && <option value="professor">📖 Professor</option>}
                    {!isClericalUser && <option value="clerical">📋 Clerical</option>}
                    {!isClericalUser && <option value="admin">🔑 Administrator</option>}
                  </select>
                  {isClericalUser && (
                    <p style={{ fontSize: '0.8em', color: '#6b7280', marginTop: '4px' }}>
                      Clerical users can only create student accounts.
                    </p>
                  )}
                </div>
                
                {(newUser.role === 'student' || newUser.role === 'professor') && (
                  <div className="form-group">
                    <label>Assign to Cohort</label>
                    <select 
                      value={newUser.cohortId}
                      onChange={(e) => setNewUser({...newUser, cohortId: e.target.value})}
                      required
                      disabled={addUserLoading}
                    >
                      <option value="">Select cohort...</option>
                      {(cohorts || []).map(cohort => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="info-box">
                  <span className="info-icon">ℹ️</span>
                  <p>
                    {newUser.method === 'invite' 
                      ? 'An invitation email will be sent. The user will click a link to set their password and activate their account.'
                      : 'A temporary password will be generated. You will need to share it with the user. They should change it after first login.'}
                  </p>
                </div>
                
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => { setShowAddModal(false); setAddUserSuccess(null); }} disabled={addUserLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={addUserLoading}>
                    {addUserLoading 
                      ? '⏳ Creating...' 
                      : newUser.method === 'invite' ? '📧 Send Invitation' : '🔑 Create User'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={closeBulkModal}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Bulk Import Students</h3>
              <button className="modal-close" onClick={closeBulkModal}>×</button>
            </div>

            {/* Step 1: Input */}
            {bulkStep === 'input' && (
              <div style={{ padding: '0 24px 24px' }}>
                <div className="form-group">
                  <label>Assign to Cohort</label>
                  <select
                    value={bulkCohortId}
                    onChange={(e) => setBulkCohortId(e.target.value)}
                  >
                    <option value="">No cohort (assign later)</option>
                    {(cohorts || []).map(cohort => (
                      <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Paste Student List</label>
                  <textarea
                    rows={10}
                    placeholder={`Paste names and emails, one per line. Accepted formats:\n\nJane Smith, jane@example.com\nJohn Doe\tjohn@example.com\njane@example.com\n\nYou can copy/paste directly from Excel or Google Sheets (Name column + Email column).`}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: '0.9em', lineHeight: '1.6' }}
                  />
                </div>

                <div className="info-box">
                  <span className="info-icon">💡</span>
                  <p>
                    Each student will receive an invitation email to set their password. 
                    Supports comma-separated, tab-separated (spreadsheet paste), or email-only formats.
                  </p>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeBulkModal}>Cancel</button>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    onClick={parseBulkText}
                    disabled={!bulkText.trim()}
                  >
                    Preview Import
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Preview */}
            {bulkStep === 'preview' && (
              <div style={{ padding: '0 24px 24px' }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                    <span style={{ padding: '4px 12px', borderRadius: 12, background: '#e8f5e9', color: '#2e7d32', fontSize: '0.85em', fontWeight: 600 }}>
                      ✅ {bulkParsed.filter(p => p.status === 'ready').length} ready to import
                    </span>
                    {bulkParsed.filter(p => p.status === 'skip').length > 0 && (
                      <span style={{ padding: '4px 12px', borderRadius: 12, background: '#fff3e0', color: '#e65100', fontSize: '0.85em', fontWeight: 600 }}>
                        ⏭️ {bulkParsed.filter(p => p.status === 'skip').length} already exist
                      </span>
                    )}
                    {bulkParsed.filter(p => p.status === 'error').length > 0 && (
                      <span style={{ padding: '4px 12px', borderRadius: 12, background: '#fce4ec', color: '#c62828', fontSize: '0.85em', fontWeight: 600 }}>
                        ❌ {bulkParsed.filter(p => p.status === 'error').length} errors
                      </span>
                    )}
                  </div>
                  {bulkCohortId && (
                    <p style={{ fontSize: '0.85em', color: '#666' }}>
                      Cohort: <strong>{getCohortName(bulkCohortId)}</strong>
                    </p>
                  )}
                </div>

                <div style={{ maxHeight: 350, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                  <table className="data-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkParsed.map((entry, i) => (
                        <tr key={i} style={{ opacity: entry.status === 'skip' ? 0.5 : 1 }}>
                          <td>{entry.name || <span style={{ color: '#aaa' }}>--</span>}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{entry.email || '--'}</td>
                          <td>
                            {entry.status === 'ready' && <span style={{ color: '#2e7d32' }}>✅ Ready</span>}
                            {entry.status === 'skip' && <span style={{ color: '#e65100' }}>⏭️ {entry.error}</span>}
                            {entry.status === 'error' && <span style={{ color: '#c62828' }}>❌ {entry.error}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setBulkStep('input')}>
                    ← Back
                  </button>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    onClick={handleBulkImport}
                    disabled={bulkParsed.filter(p => p.status === 'ready').length === 0}
                  >
                    📧 Send {bulkParsed.filter(p => p.status === 'ready').length} Invitation{bulkParsed.filter(p => p.status === 'ready').length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Importing */}
            {bulkStep === 'importing' && (
              <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                <h3 style={{ marginBottom: 8 }}>Sending Invitations...</h3>
                <p style={{ color: '#666', marginBottom: 16 }}>Please do not close this window.</p>
                <div style={{ 
                  width: '100%', height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${bulkProgress}%`, height: '100%', 
                    background: 'linear-gradient(90deg, #4f46e5, #6366f1)', 
                    borderRadius: 4, transition: 'width 0.3s ease' 
                  }} />
                </div>
                <p style={{ fontSize: '0.85em', color: '#888', marginTop: 8 }}>{bulkProgress}% complete</p>
              </div>
            )}

            {/* Step 4: Done */}
            {bulkStep === 'done' && (
              <div style={{ padding: '0 24px 24px' }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                  <span style={{ padding: '4px 12px', borderRadius: 12, background: '#e8f5e9', color: '#2e7d32', fontSize: '0.85em', fontWeight: 600 }}>
                    ✅ {bulkResults.filter(r => r.status === 'success').length} imported
                  </span>
                  {bulkResults.filter(r => r.status === 'error').length > 0 && (
                    <span style={{ padding: '4px 12px', borderRadius: 12, background: '#fce4ec', color: '#c62828', fontSize: '0.85em', fontWeight: 600 }}>
                      ❌ {bulkResults.filter(r => r.status === 'error').length} failed
                    </span>
                  )}
                  {bulkResults.filter(r => r.status === 'skip').length > 0 && (
                    <span style={{ padding: '4px 12px', borderRadius: 12, background: '#fff3e0', color: '#e65100', fontSize: '0.85em', fontWeight: 600 }}>
                      ⏭️ {bulkResults.filter(r => r.status === 'skip').length} skipped
                    </span>
                  )}
                </div>

                <div style={{ maxHeight: 350, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                  <table className="data-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.map((entry, i) => (
                        <tr key={i}>
                          <td>{entry.name || '--'}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{entry.email || '--'}</td>
                          <td>
                            {entry.status === 'success' && <span style={{ color: '#2e7d32' }}>✅ Invited</span>}
                            {entry.status === 'skip' && <span style={{ color: '#e65100' }}>⏭️ {entry.error}</span>}
                            {entry.status === 'error' && <span style={{ color: '#c62828' }}>❌ {entry.error}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeBulkModal}>
                    Done
                  </button>
                  <button type="button" className="btn-primary" onClick={resetBulkModal}>
                    Import More
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleEditUser}>
              <div className="form-group">
                <label>Full Name</label>
                <input 
                  type="text"
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Email Address</label>
                <input 
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Role</label>
                <select
                  value={editingUser.role || 'student'}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                  disabled={isClericalUser}
                >
                  <option value="student">🎓 Student</option>
                  {!isClericalUser && <option value="professor">📖 Professor</option>}
                  {!isClericalUser && <option value="clerical">📋 Clerical</option>}
                  {!isClericalUser && <option value="admin">🔑 Administrator</option>}
                </select>
                {isClericalUser && (
                  <p style={{ fontSize: '0.8em', color: '#6b7280', marginTop: '4px' }}>
                    Clerical users cannot change roles.
                  </p>
                )}
              </div>
              
              {(editingUser.role === 'student' || editingUser.role === 'professor') && (
                <div className="form-group">
                  <label>Cohort</label>
                  <select 
                    value={editingUser.cohortId || ''}
                    onChange={(e) => setEditingUser({...editingUser, cohortId: e.target.value})}
                  >
                    <option value="">Not assigned</option>
                    {(cohorts || []).map(cohort => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="form-group">
                <label>Status</label>
                <select 
                  value={editingUser.status || 'active'}
                  onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                >
                  <option value="active">✅ Active</option>
                  <option value="inactive">⏸️ Inactive</option>
                </select>
              </div>
              
              {(editingUser.role === 'student') && (
                <div className="form-group">
                  <label>NCLEX Test Date</label>
                  <input
                    type="date"
                    value={editingUser.nclex_test_date || ''}
                    onChange={(e) => setEditingUser({...editingUser, nclex_test_date: e.target.value})}
                  />
                </div>
              )}

              {/* Social Media Handles
                  Editable by administrators and clerical staff. The Edit
                  User modal is already gated so clerical users can only
                  open it for students, and the LMSContext.updateStudent
                  function enforces the same role check server-side. */}
              {(editingUser.role === 'student') && (
                <>
                  <div className="form-group">
                    <label>TikTok Username</label>
                    <input
                      type="text"
                      placeholder="e.g. codebreakerlms (without the @)"
                      value={editingUser.tiktok_handle || ''}
                      onChange={(e) => setEditingUser({...editingUser, tiktok_handle: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label>Instagram Username</label>
                    <input
                      type="text"
                      placeholder="e.g. codebreakerlms (without the @)"
                      value={editingUser.instagram_handle || ''}
                      onChange={(e) => setEditingUser({...editingUser, instagram_handle: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label>Facebook Username / Profile ID</label>
                    <input
                      type="text"
                      placeholder="e.g. codebreakerlms"
                      value={editingUser.facebook_handle || ''}
                      onChange={(e) => setEditingUser({...editingUser, facebook_handle: e.target.value})}
                    />
                  </div>
                </>
              )}

              <div className="password-reset-section">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => handlePasswordReset(editingUser.email)}
                >
                  📧 Send Password Reset Email
                </button>
                {resetEmailSent && (
                  <p className="success-message">✅ Password reset email sent to {editingUser.email}</p>
                )}
                {resetEmailError && (
                  <p className="error-message">❌ {resetEmailError}</p>
                )}
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Profile Modal */}
      {showProfileModal && viewingUser && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal profile-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Student Profile</h3>
              <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Avatar and Name */}
              <div className="profile-header-section">
                {viewingUser.avatar_url ? (
                  <img src={viewingUser.avatar_url} alt={viewingUser.name} className="profile-avatar-large" />
                ) : (
                  <div className="profile-avatar-placeholder large">
                    {(viewingUser.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                )}
                <div className="profile-name-section">
                  <h2>{viewingUser.name}</h2>
                  <p>{viewingUser.email}</p>
                  <span className={`role-badge ${viewingUser.role}`}>
                    {viewingUser.role === 'admin' ? '🔑 Admin'
                      : viewingUser.role === 'professor' ? '📖 Professor'
                      : viewingUser.role === 'clerical' ? '📋 Clerical'
                      : '🎓 Student'}
                  </span>
                </div>
              </div>

              {/* Cohort Info */}
              {(viewingUser.role === 'student' || viewingUser.role === 'professor') && (
                <div className="profile-info-section">
                  <h4>Cohort</h4>
                  <p>{getCohortName(viewingUser.cohortId) || 'Not assigned'}</p>
                </div>
              )}

              {/* NCLEX Test Date */}
              {viewingUser.role === 'student' && (
                <div className="profile-info-section">
                  <h4>NCLEX Test Date</h4>
                  <p>
                    {viewingUser.nclex_test_date
                      ? new Date(viewingUser.nclex_test_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                      : 'Not set'}
                  </p>
                </div>
              )}

              {/* Social Media */}
              <div className="profile-info-section">
                <h4>Social Media</h4>
                <div className="social-links">
                  {viewingUser.tiktok_handle ? (
                    <a href={`https://tiktok.com/@${viewingUser.tiktok_handle}`} target="_blank" rel="noopener noreferrer" className="social-link tiktok">
                      <span className="social-icon">📱</span>
                      <span>@{viewingUser.tiktok_handle}</span>
                    </a>
                  ) : (
                    <span className="social-link empty">
                      <span className="social-icon">📱</span>
                      <span>TikTok not set</span>
                    </span>
                  )}
                  
                  {viewingUser.instagram_handle ? (
                    <a href={`https://instagram.com/${viewingUser.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="social-link instagram">
                      <span className="social-icon">📸</span>
                      <span>@{viewingUser.instagram_handle}</span>
                    </a>
                  ) : (
                    <span className="social-link empty">
                      <span className="social-icon">📸</span>
                      <span>Instagram not set</span>
                    </span>
                  )}
                  
                  {viewingUser.facebook_handle ? (
                    <a href={`https://facebook.com/${viewingUser.facebook_handle}`} target="_blank" rel="noopener noreferrer" className="social-link facebook">
                      <span className="social-icon">👥</span>
                      <span>{viewingUser.facebook_handle}</span>
                    </a>
                  ) : (
                    <span className="social-link empty">
                      <span className="social-icon">👥</span>
                      <span>Facebook not set</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Account Info */}
              <div className="profile-info-section">
                <h4>Account Info</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Status</span>
                    <span className={`status-badge ${viewingUser.status || 'active'}`}>
                      {viewingUser.status === 'active' ? '● Active' : '○ Inactive'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Joined</span>
                    <span>{formatDate(viewingUser.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowProfileModal(false)}>
                Close
              </button>
              <button type="button" className="btn-primary" onClick={() => {
                setShowProfileModal(false);
                openEditModal(viewingUser);
              }}>
                Edit User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
